/**
 * Are we online? `navigator.onLine` and its events, plus the service worker's word: it
 * tells the page when the daily api didn't answer and a cached copy was served, which
 * `navigator.onLine` misses on a bad signal. Started once at boot (main.tsx) so a
 * message that arrives before Today mounts isn't lost. React reads it via `useOnline`.
 */
import { useSyncExternalStore } from 'react';

let online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
const listeners = new Set<() => void>();
const set = (v: boolean) => {
	if (v === online) return;
	online = v;
	for (const fn of listeners) fn();
};

let started = false;
export function initNetwork(): void {
	if (started || typeof window === 'undefined') return;
	started = true;
	window.addEventListener('online', () => set(true));
	window.addEventListener('offline', () => set(false));
	try {
		navigator.serviceWorker?.addEventListener('message', (e: MessageEvent) => {
			const type = (e.data as { type?: unknown } | null)?.type;
			if (type === 'OFFLINE') set(false);
			else if (type === 'ONLINE') set(navigator.onLine !== false);
		});
		navigator.serviceWorker?.startMessages();
	} catch {
		// No service worker: navigator.onLine alone.
	}
}

export function useOnline(): boolean {
	return useSyncExternalStore(
		(fn) => {
			listeners.add(fn);
			return () => listeners.delete(fn);
		},
		() => online,
		() => true,
	);
}
